import React, { ReactElement, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import {
    createFailure,
    createListTableColumns,
    createValidationResult,
    depascalize,
    ensure,
    FailureSeverity,
    FormDialog,
    FormFields,
    FormValidationSummary,
    Icon,
    ShimmeredDetailsList,
    Spinner,
    SpinnerSize,
    Stack,
    useNotifications,
    ValidationFailure
} from "sprint-common-web";

import { OrderStatus } from "../../common/types/OrderStatus";
import { useOrdersModuleApi } from "../../common/useOrdersModuleApi";
import { OrderListingContext } from "../listing/context/OrderListingContext";
import { OrderListingItem } from "../listing/OrderListingItem";

interface RemoveOrderItem {
    id: string;
    content: string;
    pONumber: string;
    orderStatus: OrderStatus;
    buzzId?: string;
}

type ProcessRemoveOrderItem = RemoveOrderItem & {
    processStatus: ActionStatus;
};

interface DeleteMultipleOrdersDialogProps {
    selectedItems: RemoveOrderItem[];
    onRemovingComplete(successfullyRemovedOrders: RemoveOrderItem[]): void;
}

enum ActionStatus {
    WaitingForConfirmation,
    InProgress,
    Completed,
    Failed
}

export const DeleteMultipleOrdersDialog: React.VFC<DeleteMultipleOrdersDialogProps> = ({
    selectedItems,
    onRemovingComplete
}) => {
    const { deleteOrder } = useOrdersModuleApi();

    const { deleteOrder: deleteOrderFromContext } = useContext(OrderListingContext);

    const { success, error: errorNotification } = useNotifications();

    const { formState, handleSubmit } = useForm();

    const [isActionInProgress, setIsActionInProgress] = useState<boolean>(false);
    const [items, setItems] = useState<ProcessRemoveOrderItem[]>(
        selectedItems
            .filter((selectedItem) => isOrderItemRemovable(selectedItem))
            .map((item) => ({ ...item, processStatus: ActionStatus.WaitingForConfirmation }))
    );

    const setStatusById = (id: string, status: ActionStatus) => {
        setItems((prevItems) =>
            prevItems.map((prevItem) => (prevItem.id === id ? { ...prevItem, processStatus: status } : prevItem))
        );
    };

    const formHasErrors = createValidationResult([validateDiscardedItems(items, selectedItems.length)]);
    const areValidOrdersEmpty = !items.length;

    const handleRemovingComplete = () => {
        const removingCompletedItems = items.filter(({ processStatus }) => processStatus === ActionStatus.Completed);

        onRemovingComplete(removingCompletedItems.map(({ processStatus, ...item }) => item));
    };

    const remove = async () => {
        const results = await removeAllItems();

        if (results.every(({ status }) => status === "fulfilled")) {
            onRemovingComplete(items.map(({ processStatus, ...item }) => item));

            success("Removing complete");
        } else errorNotification("Something went wrong while removing orders");
    };

    const removeAllItems = async () => {
        setIsActionInProgress(true);

        const results = await Promise.allSettled(
            items.map(async (item) => {
                setStatusById(item.id, ActionStatus.InProgress);

                try {
                    await deleteOrder({ orderId: item.id });

                    setStatusById(item.id, ActionStatus.Completed);

                    deleteOrderFromContext(item.id);
                } catch (error) {
                    setStatusById(item.id, ActionStatus.Failed);

                    throw error;
                }
            })
        );

        setIsActionInProgress(false);

        return results;
    };

    return (
        <Stack>
            <FormDialog
                title={"Remove orders"}
                formState={formState}
                text={"Are you sure you want to start removing listed orders?"}
                isBlocking={isActionInProgress}
                showCloseButton={false}
                submit={{
                    onClick: handleSubmit(remove),
                    disabled: areValidOrdersEmpty || isActionInProgress
                }}
                cancel={{
                    onClick: handleRemovingComplete
                }}
                width={700}
                contentHeight={400}
            >
                <FormFields>
                    <FormValidationSummary validationResult={formHasErrors} />
                </FormFields>
                {!!items.length && <ItemsSummary items={items} />}
            </FormDialog>
        </Stack>
    );
};

const orderPendingStr = depascalize(OrderStatus[OrderStatus.Pending]);
const orderRejectedStr = depascalize(OrderStatus[OrderStatus.Rejected]);
const orderCompletedStr = depascalize(OrderStatus[OrderStatus.DeliveryCompleted]);
const discardedOrdersInfoMessage = `Orders with status other than '${orderPendingStr}', '${orderRejectedStr}' and '${orderCompletedStr}', and those coming from Buzz or Shreq have been discarded`;

const validateDiscardedItems = (items: RemoveOrderItem[], selectedItemsCount: number): ValidationFailure | void => {
    if (!items.length) {
        return createFailure(
            `${discardedOrdersInfoMessage}. Nothing to remove.`,
            "no-orders-to-deliver",
            FailureSeverity.Error
        );
    } else if (items.length < selectedItemsCount) {
        return createFailure(discardedOrdersInfoMessage, "orders-discarded-from-delivery", FailureSeverity.Warning);
    }
};

interface ItemsSummaryProps {
    items: ProcessRemoveOrderItem[];
}

const ItemsSummary: React.VFC<ItemsSummaryProps> = ({ items }: ItemsSummaryProps) => {
    const columns = createListTableColumns<ProcessRemoveOrderItem>([
        {
            type: "basic",
            key: "pONumber",
            name: "PO number",
            minWidth: 200
        },
        {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 200
        },
        {
            type: "basic",
            key: "processStatus",
            name: "Process status",
            minWidth: 200,
            onRender: renderActionStatus
        }
    ]);

    return <ShimmeredDetailsList columns={columns} items={items} layoutMode="fixedColumns" />;
};

export const renderActionStatus = (item?: ProcessRemoveOrderItem): ReactElement | null => {
    ensure(item);
    const status = item["processStatus"];

    const indicator = (() => {
        switch (status) {
            case ActionStatus.WaitingForConfirmation: {
                return <></>;
            }

            case ActionStatus.InProgress: {
                return <Spinner size={SpinnerSize.small} />;
            }

            case ActionStatus.Completed: {
                return <Icon iconName="Accept" />;
            }

            case ActionStatus.Failed: {
                return <Icon iconName="Cancel" />;
            }
        }
    })();

    return <Stack horizontalAlign="start">{indicator}</Stack>;
};

const isOrderItemRemovable = (item: RemoveOrderItem) =>
    (item.orderStatus === OrderStatus.DeliveryCompleted ||
        item.orderStatus === OrderStatus.Rejected ||
        item.orderStatus === OrderStatus.Pending) &&
    !item.buzzId;

export const mapToRemoveOrderItem = (order: OrderListingItem): RemoveOrderItem => {
    return {
        id: order.id,
        content: order.content,
        pONumber: order.pONumber,
        orderStatus: order.status,
        buzzId: order.buzzId
    };
};
